'use client';

import * as React from 'react';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';
import { focusVisibleOutline } from '@/lib/focus-styles';
import { cn } from '@/lib/utils';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
    className,
    classNames,
    showOutsideDays = true,
    captionLayout = 'label',
    navLayout,
    locale,
    formatters,
    components,
    ...props
}: CalendarProps): React.JSX.Element {
    const defaultClassNames = getDefaultClassNames();
    const isDropdownCaption =
        captionLayout === 'dropdown' ||
        captionLayout === 'dropdown-months' ||
        captionLayout === 'dropdown-years';
    const isNavAround = navLayout === 'around';
    const isNavAfter = navLayout === 'after';
    const usesCaptionNav = isNavAround || isNavAfter;

    return (
        <DayPicker
            showOutsideDays={showOutsideDays}
            captionLayout={captionLayout}
            navLayout={navLayout}
            locale={locale}
            className={cn('p-3', className)}
            formatters={{
                formatMonthDropdown: (date) =>
                    date.toLocaleString(locale?.code ?? 'default', { month: 'long' }),
                ...formatters,
            }}
            classNames={{
                root: cn('w-full', defaultClassNames.root),
                months: cn('relative flex flex-col gap-4', defaultClassNames.months),
                month: cn('relative flex flex-col gap-4', defaultClassNames.month),
                month_caption: cn(
                    'flex h-8 w-full items-center justify-center',
                    isNavAround ? 'px-10' : 'px-2',
                    defaultClassNames.month_caption
                ),
                dropdowns: cn(
                    'flex h-8 items-center justify-center gap-2',
                    defaultClassNames.dropdowns
                ),
                dropdown_root: cn(
                    'relative inline-flex items-center rounded-[var(--corner-4)]',
                    defaultClassNames.dropdown_root
                ),
                dropdown: cn('absolute inset-0 cursor-pointer opacity-0', defaultClassNames.dropdown),
                caption_label: cn(
                    'body-copy-m-bold select-none text-neutral-10',
                    isDropdownCaption &&
                        'flex h-8 items-center gap-1 rounded-[var(--corner-4)] px-2 [&>svg]:size-3.5 [&>svg]:text-neutral-50',
                    defaultClassNames.caption_label
                ),
                nav: cn(
                    !usesCaptionNav &&
                        'absolute inset-x-3 top-3 flex items-center justify-between',
                    isNavAfter && 'absolute inset-x-0 top-0 flex h-8 items-center justify-between',
                    defaultClassNames.nav
                ),
                button_previous: cn(
                    'inline-flex size-8 items-center justify-center rounded-[var(--corner-4)] text-neutral-10 hover:bg-sand-95 disabled:pointer-events-none disabled:opacity-50',
                    focusVisibleOutline,
                    isNavAround && 'absolute top-0 left-0 z-10',
                    defaultClassNames.button_previous
                ),
                button_next: cn(
                    'inline-flex size-8 items-center justify-center rounded-[var(--corner-4)] text-neutral-10 hover:bg-sand-95 disabled:pointer-events-none disabled:opacity-50',
                    focusVisibleOutline,
                    isNavAround && 'absolute top-0 right-0 z-10',
                    defaultClassNames.button_next
                ),
                month_grid: cn('w-full border-collapse', defaultClassNames.month_grid),
                weekdays: cn('flex', defaultClassNames.weekdays),
                weekday: cn(
                    'body-copy-s flex-1 py-2 text-center text-neutral-50',
                    defaultClassNames.weekday
                ),
                week: cn('mt-1 flex w-full', defaultClassNames.week),
                day: cn('relative flex-1 p-0 text-center', defaultClassNames.day),
                day_button: cn(
                    'body-copy-m mx-auto flex size-9 items-center justify-center rounded-[var(--corner-4)] text-neutral-10 hover:bg-sand-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
                    defaultClassNames.day_button
                ),
                outside: cn('text-neutral-50 opacity-50', defaultClassNames.outside),
                today: cn('font-semibold', defaultClassNames.today),
                selected: cn(
                    '[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary',
                    defaultClassNames.selected
                ),
                disabled: cn('opacity-50', defaultClassNames.disabled),
                ...classNames,
            }}
            components={{
                Chevron: ({ className: chevronClassName, orientation, ...chevronProps }) => {
                    if (orientation === 'left') {
                        return (
                            <ChevronLeftIcon
                                className={cn('size-4', chevronClassName)}
                                aria-hidden
                                {...chevronProps}
                            />
                        );
                    }

                    if (orientation === 'right') {
                        return (
                            <ChevronRightIcon
                                className={cn('size-4', chevronClassName)}
                                aria-hidden
                                {...chevronProps}
                            />
                        );
                    }

                    return (
                        <ChevronDownIcon
                            className={cn('size-3.5', chevronClassName)}
                            aria-hidden
                            {...chevronProps}
                        />
                    );
                },
                ...components,
            }}
            {...props}
        />
    );
}

export { Calendar };
